import { collection, addDoc, doc, setDoc, updateDoc, arrayUnion, getDoc, query, where, getDocs, serverTimestamp, orderBy, limit } from 'firebase/firestore';
import { auth, db } from '../firebase';
import ChatService from '../Chat/ChatService';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';
import { getFirebaseAuthHeaders } from '../utils/firebaseApiAuth';
import { getApiUrl, API_CONFIG } from '../config/api';

const areProjectIdsEquivalent = (idA, idB) => ProjectIdGenerator.areEquivalent(idA, idB);

class NotificationService {
  /** MailerSend assignment email — non-blocking; does not affect in-app notification. */
  static async sendAssignmentEmail(creatorId, { projectId, campaignId, briefId }, idToken = null) {
    try {
      const user = auth.currentUser;
      if (!user || !projectId || !campaignId || !briefId) return;

      const headers = idToken
        ? {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
            'X-Firebase-Token': idToken,
          }
        : await getFirebaseAuthHeaders();

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.NOTIFY_CREATOR_ASSIGNMENT), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          creatorId,
          projectId,
          campaignId,
          briefId,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        console.warn('Assignment email not sent:', data.error || response.status);
        return;
      }
      if (data.skipped) {
        console.log('Assignment email already sent for project:', projectId);
      } else {
        console.log('Assignment email sent for creator:', creatorId);
      }
    } catch (error) {
      console.warn('Assignment email error (non-critical):', error);
    }
  }

  // Create a notification for a creator when they're assigned to a brief/script
  static async notifyCreatorAssignment(creatorId, assignmentData) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const idToken = await user.getIdToken(true);

      // SECURITY: Validate creatorId parameter
      if (!creatorId || typeof creatorId !== 'string' || creatorId.trim().length === 0) {
        throw new Error('Invalid creatorId parameter: must be a non-empty string');
      }
      const normalizedCreatorId = creatorId.trim();

      // SECURITY: Verify sender is a brand/client
      const senderDoc = await getDoc(doc(db, 'users', user.uid));
      if (!senderDoc.exists()) {
        throw new Error('Unauthorized: User not found');
      }
      
      const senderData = senderDoc.data();
      const isAuthorized = senderData.userType === 'brand' || senderData.userType === 'client';
      
      if (!isAuthorized) {
        throw new Error('Unauthorized: Only brands can send assignment notifications');
      }

      // Require projectId from assignment; never generate here
      const projectId = ProjectIdGenerator.normalizeProjectId(assignmentData.projectId) || assignmentData.projectId;
      if (!projectId) {
        throw new Error('Missing projectId on assignment; refusing to generate');
      }

      // SECURITY: Validate projectId contains correct creatorId (first 8 chars)
      try {
        const parsed = ProjectIdGenerator.parseProjectId(projectId);
        const projectCreatorIdShort = parsed.creatorId; // First 8 chars
        const expectedCreatorIdShort = normalizedCreatorId.slice(0, 8).padEnd(8, '0');
        
        if (projectCreatorIdShort !== expectedCreatorIdShort) {
          console.error('🚨 SECURITY WARNING: ProjectId creator mismatch!', {
            projectId,
            projectCreatorIdShort,
            expectedCreatorIdShort,
            fullCreatorId: normalizedCreatorId
          });
          throw new Error(`ProjectId creator mismatch: projectId contains ${projectCreatorIdShort}, expected ${expectedCreatorIdShort}`);
        }
      } catch (parseError) {
        // If we can't parse, log warning but continue (might be legacy format)
        console.warn('Could not validate projectId creatorId:', parseError.message);
      }

      const resolvedBrandAvatarImage = assignmentData.brandAvatarImage
        || senderData.profileImage
        || senderData.profileImagePath
        || senderData.avatar
        || senderData.photoURL
        || senderData.brandLogo
        || null;

      const resolvedBrandAvatarColor = assignmentData.brandAvatarColor
        || senderData.brandColor
        || senderData.themeColor
        || null;

      const resolvedBrandName = assignmentData.brandName
        || senderData.brandName
        || senderData.displayName
        || senderData.companyName
        || senderData.agencyName
        || senderData.email
        || 'Brand';

      const resolvedCreativeControl = assignmentData.creativeControl
        || senderData.defaultCreativeControl
        || 'exact';

      const resolvedPlatform = assignmentData.platform
        || senderData.defaultPlatform
        || 'tiktok';

      // IMPORTANT: Fetch and snapshot the assigned scripts at assignment time
      // This ensures scripts don't change if brand updates the brief later
      let assignedScriptsSnapshot = null;
      try {
        const briefRef = doc(db, 'users', user.uid, 'campaigns', assignmentData.campaignId, 'briefs', assignmentData.briefId);
        const briefDoc = await getDoc(briefRef);
        
        if (briefDoc.exists()) {
          const briefData = briefDoc.data();
          const scriptIds = assignmentData.scriptIds || [];
          
          // Filter and create deep copy of assigned scripts
          const assignedScripts = (briefData.scripts || []).filter(script => 
            scriptIds.includes(script.id) || scriptIds.includes(String(script.id))
          );
          
          // Create a deep copy snapshot to preserve scripts at assignment time
          assignedScriptsSnapshot = JSON.parse(JSON.stringify(assignedScripts));
          console.log('📸 Created script snapshot for assignment:', {
            scriptIds,
            snapshotCount: assignedScriptsSnapshot.length
          });
        }
      } catch (error) {
        console.error('Error creating script snapshot (non-critical):', error);
        // Continue without snapshot - fallback will use current brief scripts
      }

      const notification = {
        type: 'assignment_request',
        title: 'Assignment Request',
        message: `You've been requested to work on "${assignmentData.briefTitle}" for ${assignmentData.campaignName}`,
        // REQUIRED fields for notification routing and security
        recipientId: normalizedCreatorId,
        creatorId: normalizedCreatorId,
        projectId: projectId,
        briefId: assignmentData.briefId,
        campaignId: assignmentData.campaignId,
        details: {
          projectId: projectId,
          campaignId: assignmentData.campaignId,
          campaignName: assignmentData.campaignName,
          briefId: assignmentData.briefId,
          briefTitle: assignmentData.briefTitle,
          scriptIds: assignmentData.scriptIds || [],
          assignedScriptsSnapshot: assignedScriptsSnapshot, // Store snapshot of scripts at assignment time
          productType: assignmentData.productType || 'service',
          assignedBy: user.uid,
          assignedAt: new Date().toISOString(),
          status: 'awaiting_approval',
          brandAvatarImage: resolvedBrandAvatarImage,
          brandAvatarColor: resolvedBrandAvatarColor,
          brandName: resolvedBrandName,
          creativeControl: resolvedCreativeControl,
          platform: resolvedPlatform
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      // Add notification to creator's notifications collection
      // SECURITY: Use normalizedCreatorId to ensure correct path
      const creatorNotificationsRef = collection(db, 'creators', normalizedCreatorId, 'notifications');
      await addDoc(creatorNotificationsRef, notification);

      // CRITICAL: Create assignment doc as source of truth for project ownership
      // This allows creator dashboard to verify assignment without reading brand campaign docs
      // Brands create via Admin SDK privileges (or via Cloud Function)
      try {
        const assignmentDocRef = doc(db, 'creators', normalizedCreatorId, 'assignments', projectId);
        await setDoc(assignmentDocRef, {
          projectId: projectId,
          brandUserId: user.uid,
          campaignId: assignmentData.campaignId,
          briefId: assignmentData.briefId,
          creatorId: normalizedCreatorId, // Must match the path creatorId
          status: 'awaiting_approval',
          assignedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          // Store snapshot data for offline/restricted access scenarios
          briefTitle: assignmentData.briefTitle,
          campaignName: assignmentData.campaignName,
          scriptIds: assignmentData.scriptIds || [],
          productType: assignmentData.productType || 'service'
        });
        console.log('✅ Created assignment doc for project ownership verification:', projectId);
      } catch (assignmentError) {
        // If assignment doc creation fails (e.g., permissions), log but don't block notification
        // This allows the system to work even if rules aren't perfect yet
        console.warn('⚠️ Failed to create assignment doc (non-critical):', assignmentError);
        // Continue - notification is more important, assignment doc is extra security layer
      }

      console.log('Creator notification sent:', notification);

      await NotificationService.sendAssignmentEmail(creatorId, {
        projectId,
        campaignId: assignmentData.campaignId,
        briefId: assignmentData.briefId,
      }, idToken);

      return notification;
    } catch (error) {
      console.error('Error sending creator notification:', error);
      throw error;
    }
  }

  // Create a notification for the brand when a creator accepts an assignment
  static async notifyBrandCreatorAccepted(creatorId, creatorName, assignmentData, brandUserId) {
    try {
      // Fetch the actual creator name from Firestore to ensure it's correct
      let displayCreatorName = creatorName;
      try {
        if (creatorId) {
          const creatorDoc = await getDoc(doc(db, 'users', creatorId));
          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data();
            // Prefer displayName (set during onboarding), then name, then handle, then fallback to passed creatorName
            displayCreatorName = creatorData.displayName 
              || creatorData.name 
              || creatorData.handle 
              || creatorName;
            console.log('✅ Notification: Using creator name from Firestore:', displayCreatorName, 'from creatorId:', creatorId);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching creator name for notification:', error);
        // Fallback to passed creatorName
        displayCreatorName = creatorName;
      }

      // Get brief data to fetch script names
      const briefRef = doc(db, 'users', brandUserId, 'campaigns', assignmentData.campaignId, 'briefs', assignmentData.briefId);
      const briefDoc = await getDoc(briefRef);
      
      let scriptNames = [];
      let productType = assignmentData.productType || 'service';
      
      if (briefDoc.exists()) {
        const briefData = briefDoc.data();
        const scripts = briefData.scripts || [];
        const scriptIds = assignmentData.scriptIds || [];
        
        // Get script names from the brief's scripts array
        // Convert both to strings for comparison to handle type mismatches
        scriptNames = scripts
          .filter(script => {
            const scriptIdStr = String(script.id || '');
            return scriptIds.some(id => String(id) === scriptIdStr);
          })
          .map(script => script.name || script.title || script.scriptName || 'Untitled Script');
        
        // Get productType from the creator assignment in the brief
        const creators = briefData.creators || [];
        const creatorAssignment = creators.find(c => c.id === creatorId);
        if (creatorAssignment && creatorAssignment.productType) {
          productType = creatorAssignment.productType;
        }
      }
      
      // Build message with brief and script names using the fetched creator name
      let message = `${displayCreatorName} accepted brief "${assignmentData.briefTitle || assignmentData.briefName || 'Untitled Brief'}"`;
      
      if (scriptNames.length > 0) {
        if (scriptNames.length === 1) {
          message += `, script "${scriptNames[0]}"`;
        } else {
          message += `, scripts "${scriptNames.join('", "')}"`;
        }
      }
      
      // Add shipping message for products
      if (productType === 'product') {
        message += '. Please ship product within 3 days';
      }
      
      // Check if notification already exists to prevent duplicates
      const notificationsRef = collection(db, 'notifications');
      try {
        const existingNotificationsQuery = query(
          notificationsRef,
          where('userId', '==', brandUserId),
          where('type', '==', 'creator'),
          where('details.creatorId', '==', creatorId),
          where('details.briefId', '==', assignmentData.briefId)
        );

        const existingSnapshot = await getDocs(existingNotificationsQuery);
        const now = Date.now();
        const hasRecentNotification = existingSnapshot.docs.some((docSnap) => {
          const notif = docSnap.data();
          if (!notif.createdAt) return false;
          const notifTime = notif.createdAt?.toDate ? notif.createdAt.toDate().getTime() : new Date(notif.createdAt).getTime();
          // Check if notification was created in the last 5 minutes
          return (now - notifTime) < 5 * 60 * 1000;
        });

        if (hasRecentNotification) {
          console.log('ℹ️ Notification already exists for this acceptance, skipping...');
          return null;
        }
      } catch (error) {
        // If query fails (e.g., missing index), log but continue to create notification
        console.warn('Could not check for existing notifications (this is okay):', error.message);
      }

      const notification = {
        userId: brandUserId,
        type: 'creator',
        title: 'Creator Accepted Assignment',
        message: message,
        details: {
          creatorId,
          creatorName: displayCreatorName,
          campaignId: assignmentData.campaignId,
          campaignName: assignmentData.campaignName,
          briefId: assignmentData.briefId,
          briefTitle: assignmentData.briefTitle || assignmentData.briefName,
          scriptIds: assignmentData.scriptIds || [],
          scriptNames: scriptNames,
          productType: productType,
          acceptedAt: new Date().toISOString()
        },
        read: false,
        createdAt: serverTimestamp()
      };

      // Add notification to root notifications collection
      await addDoc(notificationsRef, notification);

      console.log('✅ Brand notification sent:', notification);
      return notification;
    } catch (error) {
      console.error('Error sending brand notification:', error);
      throw error;
    }
  }

  // Create a notification when a creator submits work
  static async notifyBrandWorkSubmitted(creatorId, creatorName, submissionData) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      // Fetch the actual creator name from Firestore to ensure it's correct
      let displayCreatorName = creatorName;
      try {
        if (creatorId) {
          const creatorDoc = await getDoc(doc(db, 'users', creatorId));
          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data();
            // Prefer displayName (set during onboarding), then name, then handle, then fallback to passed creatorName
            displayCreatorName = creatorData.displayName 
              || creatorData.name 
              || creatorData.handle 
              || creatorName;
            console.log('✅ Notification: Using creator name from Firestore:', displayCreatorName, 'from creatorId:', creatorId);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching creator name for notification:', error);
        // Fallback to passed creatorName
        displayCreatorName = creatorName;
      }

      const notification = {
        userId: user.uid,
        type: 'campaign',
        title: 'Work Submitted',
        message: `${displayCreatorName} has submitted work for "${submissionData.briefTitle}"`,
        details: {
          creatorId,
          creatorName: displayCreatorName,
          campaignId: submissionData.campaignId,
          campaignName: submissionData.campaignName,
          briefId: submissionData.briefId,
          briefTitle: submissionData.briefTitle,
          scriptId: submissionData.scriptId,
          submittedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      // Add notification to root notifications collection
      const brandNotificationsRef = collection(db, 'notifications');
      await addDoc(brandNotificationsRef, notification);

      console.log('Brand notification sent:', notification);
      return notification;
    } catch (error) {
      console.error('Error sending brand notification:', error);
      throw error;
    }
  }

  // Handle creator accepting an assignment
  static async handleCreatorAcceptance(creatorId, assignmentData, brandUserId) {
    try {
      // Update the brief to change creator status from 'awaiting_approval' to 'confirmed'
      const briefRef = doc(db, 'users', brandUserId, 'campaigns', assignmentData.campaignId, 'briefs', assignmentData.briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (briefDoc.exists()) {
        const briefData = briefDoc.data();
        const creators = briefData.creators || [];
        
        // Find and update ONLY the matching assignment (id + projectId)
        const targetProjectId = assignmentData.projectId ? ProjectIdGenerator.normalizeProjectId(assignmentData.projectId) : null;
        const updatedCreators = creators.map(creator => {
          const matchesCreator = creator.id === creatorId;
          const matchesProject = targetProjectId ? areProjectIdsEquivalent(creator.projectId, targetProjectId) : true;
          if (matchesCreator && matchesProject) {
            return {
              ...creator,
              status: 'confirmed', // Changed from 'accepted' to 'confirmed'
              acceptedAt: new Date().toISOString()
            };
          }
          return creator;
        });
        
        await updateDoc(briefRef, {
          creators: updatedCreators
        });
      }

      // Send notification to brand that creator accepted
      await NotificationService.notifyBrandCreatorAccepted(creatorId, assignmentData.creatorName, assignmentData, brandUserId);

      // Auto-create chat between brand and creator
      try {
        // Get brand user data for brand name
        const brandUserRef = doc(db, 'users', brandUserId);
        const brandUserDoc = await getDoc(brandUserRef);
        const brandName = brandUserDoc.exists() ? (brandUserDoc.data().displayName || brandUserDoc.data().email || 'Brand') : 'Brand';

        await ChatService.createChat({
          brandUserId: brandUserId,
          creatorId: creatorId,
          briefId: assignmentData.briefId,
          campaignId: assignmentData.campaignId,
          brandName: brandName,
          creatorName: assignmentData.creatorName,
          briefName: assignmentData.briefName || 'Brief',
          campaignName: assignmentData.campaignName || 'Campaign',
          projectId: assignmentData.projectId
        });
        console.log('Chat created for confirmed creator:', creatorId);
      } catch (chatError) {
        console.error('Error creating chat:', chatError);
        // Don't throw - chat creation failure shouldn't block the acceptance
      }

      console.log('Creator acceptance processed:', creatorId);
    } catch (error) {
      console.error('Error processing creator acceptance:', error);
      throw error;
    }
  }

  // Mark creator as awaiting product/service (after confirmation)
  static async markCreatorAwaitingProduct(creatorId, briefId, campaignId, brandUserId) {
    try {
      const briefRef = doc(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs', briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (briefDoc.exists()) {
        const briefData = briefDoc.data();
        const creators = briefData.creators || [];
        
        const updatedCreators = creators.map(creator => {
          if (creator.id === creatorId && creator.status === 'confirmed') {
            return {
              ...creator,
              status: 'awaiting_product',
              awaitingProductSince: new Date().toISOString()
            };
          }
          return creator;
        });
        
        await updateDoc(briefRef, {
          creators: updatedCreators
        });
      }

      console.log('Creator marked as awaiting product:', creatorId);
    } catch (error) {
      console.error('Error marking creator as awaiting product:', error);
      throw error;
    }
  }

  // Handle creator rejecting an assignment
  static async handleCreatorRejection(creatorId, assignmentData) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      // Update the brief to change creator status from 'awaiting_approval' to 'rejected'
      const briefRef = doc(db, 'users', user.uid, 'campaigns', assignmentData.campaignId, 'briefs', assignmentData.briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (briefDoc.exists()) {
        const briefData = briefDoc.data();
        const creators = briefData.creators || [];
        
        // Find and update ONLY the matching assignment (id + projectId)
        const targetProjectId = assignmentData.projectId ? ProjectIdGenerator.normalizeProjectId(assignmentData.projectId) : null;
        const updatedCreators = creators.map(creator => {
          const matchesCreator = creator.id === creatorId;
          const matchesProject = targetProjectId ? areProjectIdsEquivalent(creator.projectId, targetProjectId) : true;
          if (matchesCreator && matchesProject) {
            // Reset carry-over fields for this assignment only
            const resetProgress = {
              ...(creator.progress || {}),
              productSent: false,
              productReceived: false,
              trackingNumber: null,
              deliveryCompany: null,
              shippedAt: null
            };
            return {
              ...creator,
              status: 'rejected',
              rejectedAt: new Date().toISOString(),
              // Keep projectId for traceability; do NOT null it
              productSentAt: null,
              deliveryAddress: null,
              progress: resetProgress
            };
          }
          return creator;
        });
        
        await updateDoc(briefRef, {
          creators: updatedCreators
        });
      }

      // Fetch the actual creator name from Firestore to ensure it's correct
      let displayCreatorName = assignmentData.creatorName;
      try {
        if (creatorId) {
          const creatorDoc = await getDoc(doc(db, 'users', creatorId));
          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data();
            // Prefer displayName (set during onboarding), then name, then handle, then fallback to passed creatorName
            displayCreatorName = creatorData.displayName 
              || creatorData.name 
              || creatorData.handle 
              || assignmentData.creatorName;
            console.log('✅ Notification: Using creator name from Firestore:', displayCreatorName, 'from creatorId:', creatorId);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching creator name for notification:', error);
        // Fallback to passed creatorName
        displayCreatorName = assignmentData.creatorName;
      }

      // Send notification to brand that creator rejected
      const notification = {
        userId: user.uid,
        type: 'creator',
        title: 'Assignment Rejected',
        message: `${displayCreatorName} has rejected the assignment for "${assignmentData.briefTitle}"`,
        details: {
          creatorId,
          creatorName: displayCreatorName,
          campaignId: assignmentData.campaignId,
          campaignName: assignmentData.campaignName,
          briefId: assignmentData.briefId,
          briefTitle: assignmentData.briefTitle,
          projectId: assignmentData.projectId || null, // Include projectId for precise matching
          rejectedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const brandNotificationsRef = collection(db, 'notifications');
      await addDoc(brandNotificationsRef, notification);

      console.log('Creator rejection processed:', creatorId);
    } catch (error) {
      console.error('Error processing creator rejection:', error);
      throw error;
    }
  }

  // Mark notification as read
  static async markNotificationAsRead(notificationId, isCreator = false) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const notificationRef = isCreator 
        ? doc(db, 'creators', user.uid, 'notifications', notificationId)
        : doc(db, 'users', user.uid, 'notifications', notificationId);

      await updateDoc(notificationRef, {
        read: true,
        readAt: new Date().toISOString()
      });

      console.log('Notification marked as read:', notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Notify brand when creator updates progress (filming, editing, submitted)
  static async notifyBrandProgressUpdate(creatorId, creatorName, progressData) {
    try {
      const { brandUserId, campaignId, campaignName, briefId, briefName, progressType, projectId } = progressData;

      if (!brandUserId || !campaignId || !briefId) {
        console.error('Missing required data for progress notification:', progressData);
        return;
      }

      // Fetch the actual creator name from Firestore to ensure it's correct
      let displayCreatorName = creatorName;
      try {
        if (creatorId) {
          const creatorDoc = await getDoc(doc(db, 'users', creatorId));
          if (creatorDoc.exists()) {
            const creatorData = creatorDoc.data();
            displayCreatorName = creatorData.displayName 
              || creatorData.name 
              || creatorData.handle 
              || creatorName;
          }
        }
      } catch (error) {
        console.error('Error fetching creator name for notification:', error);
        displayCreatorName = creatorName;
      }

      // Get brief name if not provided
      let briefTitle = briefName;
      if (!briefTitle) {
        try {
          const briefRef = doc(db, 'users', brandUserId, 'campaigns', campaignId, 'briefs', briefId);
          const briefDoc = await getDoc(briefRef);
          if (briefDoc.exists()) {
            const briefData = briefDoc.data();
            briefTitle = briefData.title || briefData.name || 'Brief';
          }
        } catch (error) {
          console.error('Error fetching brief name:', error);
          briefTitle = briefName || 'Brief';
        }
      }

      // Get campaign name if not provided
      let campaignTitle = campaignName;
      if (!campaignTitle) {
        try {
          const campaignRef = doc(db, 'users', brandUserId, 'campaigns', campaignId);
          const campaignDoc = await getDoc(campaignRef);
          if (campaignDoc.exists()) {
            const campaignData = campaignDoc.data();
            campaignTitle = campaignData.name || 'Campaign';
          }
        } catch (error) {
          console.error('Error fetching campaign name:', error);
          campaignTitle = campaignName || 'Campaign';
        }
      }

      // Determine notification message based on progress type
      let title = '';
      let message = '';
      
      switch (progressType) {
        case 'filming':
          title = 'Creator Started Filming';
          message = `${displayCreatorName} started filming for "${briefTitle}"`;
          break;
        case 'editing':
          title = 'Creator Started Editing';
          message = `${displayCreatorName} started editing content for "${briefTitle}"`;
          break;
        case 'submitted':
          title = 'Content Submitted';
          message = `${displayCreatorName} submitted content for "${briefTitle}"`;
          break;
        case 'productReceived':
          title = 'Product Received';
          message = `${displayCreatorName} received the product for "${briefTitle}"`;
          break;
        default:
          title = 'Progress Updated';
          message = `${displayCreatorName} updated their progress for "${briefTitle}"`;
      }

      const notification = {
        type: 'creator_progress',
        title: title,
        message: message,
        details: {
          creatorId,
          creatorName: displayCreatorName,
          campaignId,
          campaignName: campaignTitle,
          briefId,
          briefName: briefTitle,
          progressType,
          projectId: projectId || null,
          updatedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      // Add notification to brand's notifications collection
      const brandNotificationsRef = collection(db, 'users', brandUserId, 'notifications');
      await addDoc(brandNotificationsRef, notification);

      console.log('✅ Brand progress notification sent:', notification);
      return notification;
    } catch (error) {
      console.error('Error sending brand progress notification:', error);
      // Don't throw - notification failure shouldn't block progress update
    }
  }

  // Notify creator of approval for a project
  static async notifyCreatorOfApproval(creatorId, approvalData) {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      // SECURITY: Verify sender is a brand/client
      const senderDoc = await getDoc(doc(db, 'users', user.uid));
      if (!senderDoc.exists()) {
        throw new Error('Unauthorized: User not found');
      }
      
      const senderData = senderDoc.data();
      const isAuthorized = senderData.userType === 'brand' || senderData.userType === 'client';
      
      if (!isAuthorized) {
        throw new Error('Unauthorized: Only brands can send approval notifications');
      }

      const projectId = approvalData.projectId;
      if (!projectId) {
        throw new Error('Missing projectId on approval; refusing to generate');
      }

      const notification = {
        userId: user.uid,
        type: 'project_approval',
        title: 'Project Approved',
        message: `Your work on project "${approvalData.projectName}" has been approved!`,
        details: {
          projectId: projectId,
          projectName: approvalData.projectName,
          brandUserId: user.uid,
          approvedAt: new Date().toISOString()
        },
        read: false,
        createdAt: new Date().toISOString()
      };

      const notificationsRef = collection(db, 'creators', creatorId, 'notifications');
      await addDoc(notificationsRef, notification);

      console.log('Creator notified of approval');

      if (projectId && approvalData.allApproved) {
        try {
          await ChatService.closeChat(projectId);
          console.log('Chat closed for completed project:', projectId);
        } catch (chatError) {
          console.error('Error closing project chat after approval:', chatError);
        }
      }
    } catch (error) {
      console.error('Error notifying creator of approval:', error);
      // Don't throw - notification failure shouldn't block approval
    }
  }
}

export default NotificationService;
